import { ReplayOverlay } from '../lib/map/replayOverlay';
import { prepareReplayRoute, replayLocationStatus, replayPosition, replayHeading, replaySegmentIndex as findSegmentIndex } from '../lib/map/replayTelemetry';
import type { RoutePoint } from '../../convex/lib/gpsMeasurement';
import { replayFrame } from '../lib/map/replayGeometry';
import { MapNavigationControl } from '../lib/map/navigationControl';
import { attachApsHqModel } from '../lib/map/apsHqModel';
import { attachCookMarketplaceModel } from '../lib/map/cookMarketplaceModel';
import { attachFifteenthStreetModel } from '../lib/map/fifteenthStreetModel';
import { attachFrontierTownModel } from '../lib/map/frontierTownModel';
import { SITE_MODEL_VIEWS } from '../lib/map/siteModelFocus';
import { attachRenderQuality } from '../lib/map/renderQuality';
import '../styles/patrol-viewer.css';
import { usePaginatedQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import mapboxgl from '../lib/mapboxClient';
import 'mapbox-gl/dist/mapbox-gl.css';


function formatReplayVehicleLabel(vehicleName?: string, guardName?: string) {
  const base = (vehicleName || '').trim();
  const guardRaw = (guardName || '').trim();
  const guardLower = guardRaw.toLowerCase();
  const guard =
    guardLower === 'unknown' ||
    guardLower === 'unknown guard' ||
    /^guard\s+\d+$/i.test(guardRaw) ||
    guardRaw.includes('visit.guardName') ||
    guardRaw.includes('||')
      ? ''
      : guardRaw;
  if (guard) return base ? `${base} (${guard})` : guard;
  return base || 'Vehicle';
}

function formatReplayMarkerLabel(vehicleName?: string, guardName?: string) {
  const base = (vehicleName || '').trim();
  const guardRaw = (guardName || '').trim();
  const guardLower = guardRaw.toLowerCase();
  const guard =
    guardLower === 'unknown' ||
    guardLower === 'unknown guard' ||
    /^guard\s+\d+$/i.test(guardRaw) ||
    guardRaw.includes('visit.guardName') ||
    guardRaw.includes('||')
      ? ''
      : guardRaw;
  return guard || base || 'Vehicle';
}

const ERROR_CODES = {
  ERR_NO_ROUTE: 'PR-001: No route data available for this patrol',
  ERR_SINGLE_POINT: 'PR-002: Only one GPS point recorded (no movement tracked)',
  ERR_MAP_INIT: 'PR-003: Failed to initialize map',
  ERR_INVALID_COORDS: 'PR-004: Invalid coordinates in route data',
};

const REPLAY_BASEMAP_3D_SETTINGS = {
  show3dObjects: true,
  show3dBuildings: true,
  show3dLandmarks: false,
  show3dFacades: false,
  show3dTrees: false,
} as const;

type MapStyleMode = 'standard' | 'satellite';
type MapLightPreset = 'dawn' | 'day' | 'dusk' | 'night';
type MapLayerSelection = MapLightPreset | 'satellite';
type ReplayMapLayerOption = { id: MapLayerSelection; label: string; thumbnailUrl?: string | null };

const REPLAY_BASE_MAP_LAYER_OPTIONS: ReplayMapLayerOption[] = [
  { id: 'dawn', label: 'Dawn' },
  { id: 'day', label: 'Day' },
  { id: 'dusk', label: 'Dusk' },
  { id: 'night', label: 'Night' },
  { id: 'satellite', label: 'Satellite' },
];

interface PatrolReplayPlayerProps {
  patrolVisit: {
    _id: string;
    vehicleName: string;
    guardName?: string;
    siteName: string;
    entryTime: number;
    exitTime?: number;
    duration?: number;
    route: RoutePoint[];
    routePointCount?: number;
  };
  onClose: () => void;
  assetUrls?: {
    Vehicle_IsMoving: string | null;
    Vehicle_NotMoving: string | null;
  };
  layerThumbnails?: Partial<Record<MapLayerSelection, string | null>>;
  /** Geofence coordinates passed from the parent (avoids a redundant sites query) */
  geofenceCoords?: number[][][] | null;
  siteId?: string;
}

const FALLBACK_ASSETS = {
  Vehicle_IsMoving: '/WebAssets/Vehicle_IsMoving.png',
  Vehicle_NotMoving: '/WebAssets/Vehicle_NotMoving.png',
};

const SPEED_OPTIONS = [
  { value: 1,   label: '1×' },
  { value: 5,   label: '5×' },
  { value: 10,  label: '10×' },
  { value: 25,  label: '25×' },
  { value: 50,  label: '50×' },
  { value: 100, label: '100×' },
];

function ReplayMapStyleDropdown({
  selection,
  options,
  onChange,
}: {
  selection: MapLayerSelection;
  options: ReplayMapLayerOption[];
  onChange: (value: MapLayerSelection) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.id === selection) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <div className="style-dropdown-control replay-style-dropdown" ref={ref}>
      <button
        type="button"
        className="style-dropdown-trigger"
        aria-label={`Map style: ${selected.label}`}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <svg className="style-dropdown-trigger-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"
            fill="currentColor"
          />
        </svg>
      </button>
      <div className={`style-dropdown-menu${open ? ' open' : ''}`} role="menu">
        <div className="style-dropdown-menu-header">Map Style</div>
        <div className="style-dropdown-options-grid">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="menuitemradio"
              aria-checked={option.id === selection}
              className={`style-dropdown-option${option.id === selection ? ' active' : ''}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange(option.id);
                setOpen(false);
              }}
            >
              <div className={`style-dropdown-option-thumb${option.thumbnailUrl ? ' has-image' : ' no-image'}`}>
                {option.thumbnailUrl ? (
                  <img
                    className="style-dropdown-option-thumb-img"
                    src={option.thumbnailUrl}
                    alt={`${option.label} preview`}
                    width={56}
                    height={38}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                ) : null}
              </div>
              <span className="style-dropdown-option-label">{option.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SpeedDropdown({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = SPEED_OPTIONS.find((o) => o.value === value) ?? SPEED_OPTIONS[2];

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <div className="ec-dropdown replay-speed-dropdown" ref={ref}>
      <button
        type="button"
        className={`ec-dropdown-trigger${open ? ' ec-dropdown-trigger--open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Playback speed"
      >
        <span className="ec-dropdown-trigger-label">{selected.label}</span>
        <svg className="ec-dropdown-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="ec-dropdown-panel ec-dropdown-panel--up" role="listbox" aria-label="Playback speed">
          {SPEED_OPTIONS.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`ec-dropdown-option${opt.value === value ? ' ec-dropdown-option--active' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              <span className="ec-dropdown-option-indicator" aria-hidden="true" />
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const PatrolReplayPlayer: React.FC<PatrolReplayPlayerProps> = ({
  patrolVisit,
  onClose,
  assetUrls,
  layerThumbnails,
  geofenceCoords,
  siteId,
}) => {
  const [entered, setEntered] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);
  const modelsRef = useRef<ReturnType<typeof attachApsHqModel>[]>([]);
  const modeRef = useRef<MapStyleMode>('standard');
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => setEntered(true), window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220);
    const previousFocus = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => { window.clearTimeout(timer); previousFocus?.focus(); };
  }, []);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [followingVehicle, setFollowingVehicle] = useState(false);
  const followingVehicleRef = useRef(false);
  const overviewFrameRef = useRef<ReturnType<typeof replayFrame> | null>(null);
  const overviewPitchRef = useRef(25);
  const markerRef = useRef<ReplayOverlay | null>(null);
  const animationRef = useRef<number | null>(null);
  const currentTimeRef = useRef(0);
  const isPlayingRef = useRef(false);
  const lastHeadingRef = useRef(0);
  const isMovingRef = useRef(true);

  const markerLabel = formatReplayMarkerLabel(patrolVisit.vehicleName, patrolVisit.guardName);
  const vehicleLabel = formatReplayVehicleLabel(patrolVisit.vehicleName, patrolVisit.guardName);
  const movingVehicleIconUrl = assetUrls?.Vehicle_IsMoving || FALLBACK_ASSETS.Vehicle_IsMoving;
  const stoppedVehicleIconUrl = assetUrls?.Vehicle_NotMoving || FALLBACK_ASSETS.Vehicle_NotMoving;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const [mapStyleMode, setMapStyleMode] = useState<MapStyleMode>('standard');
  const [lightPreset, setLightPreset] = useState<MapLightPreset>('dusk');

  const needsChunks = (patrolVisit.routePointCount ?? 0) > patrolVisit.route.length;
  const { results: chunks, status: chunkStatus, loadMore } = usePaginatedQuery(
    api.patrolRoutes.listChunks,
    needsChunks ? { visitId: patrolVisit._id as Id<"patrolVisits"> } : 'skip',
    { initialNumItems: 16 },
  );
  useEffect(() => {
    if (needsChunks && chunkStatus === 'CanLoadMore') loadMore(16);
  }, [needsChunks, chunkStatus, loadMore]);
  const routeLoading = needsChunks && chunkStatus !== 'Exhausted';
  const route = useMemo(() => routeLoading ? [] : prepareReplayRoute([
    ...patrolVisit.route,
    ...(needsChunks ? chunks.flatMap(chunk => chunk.points) : []),
  ]), [routeLoading, patrolVisit.route, needsChunks, chunks]);
  const hasRoute = route.length > 0;
  const locationStatus = replayLocationStatus(currentTime, route);
  const inGpsGap = hasRoute && replayPosition(currentTime, route) === null;
  const routeDuration = hasRoute ? route[route.length - 1].timestamp - route[0].timestamp : 0;
  const currentMapLayerSelection: MapLayerSelection = mapStyleMode === 'satellite' ? 'satellite' : lightPreset;
  const replayLayerOptions = useMemo<ReplayMapLayerOption[]>(
    () =>
      REPLAY_BASE_MAP_LAYER_OPTIONS.map((option) => ({
        ...option,
        thumbnailUrl: layerThumbnails?.[option.id] || null,
      })),
    [layerThumbnails]
  );

  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const formatActualTime = (timestamp: number) =>
    new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  const getPositionAtTime = useCallback(replayPosition, []);

  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  // Initialize map
  useEffect(() => {
    if (!entered || !mapContainerRef.current || mapRef.current) return;
    if (routeLoading) return;
    if (!hasRoute) { setError(ERROR_CODES.ERR_NO_ROUTE); return; }
    setError(null);
    if (route.length === 1) { setError(ERROR_CODES.ERR_SINGLE_POINT); return; }

    const invalidCoords = route.some(
      (p) =>
        isNaN(p.latitude) || isNaN(p.longitude) ||
        p.latitude < -90 || p.latitude > 90 ||
        p.longitude < -180 || p.longitude > 180
    );
    if (invalidCoords) { setError(ERROR_CODES.ERR_INVALID_COORDS); return; }

    setMapReady(false);

    let disposeQuality: (() => void) | undefined;
    let resizeObserver: ResizeObserver | undefined;

    try {
      const allLngs = route.map((p) => p.longitude);
      const allLats = route.map((p) => p.latitude);
      if (siteId && SITE_MODEL_VIEWS[siteId]) {
        for (const [lng, lat] of SITE_MODEL_VIEWS[siteId].footprint) { allLngs.push(lng); allLats.push(lat); }
      }
      if (geofenceCoords && geofenceCoords[0]) {
        geofenceCoords[0].forEach((coord: number[]) => {
          allLngs.push(coord[0]);
          allLats.push(coord[1]);
        });
      }

      const bounds = new mapboxgl.LngLatBounds(
        [Math.min(...allLngs), Math.min(...allLats)],
        [Math.max(...allLngs), Math.max(...allLats)]
      );

      const pitch = siteId && SITE_MODEL_VIEWS[siteId] ? 40 : 25;
      const frame = replayFrame(allLngs.map((lng, i) => [lng, allLats[i]]),
        mapContainerRef.current.clientWidth, mapContainerRef.current.clientHeight, pitch);

      overviewFrameRef.current = frame;
      overviewPitchRef.current = pitch;

      const styleUrl =
        mapStyleMode === 'satellite'
          ? 'mapbox://styles/mapbox/standard-satellite'
          : 'mapbox://styles/mapbox/standard';

      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: { version: 8, imports: [{ id: 'basemap', url: styleUrl }], sources: {}, layers: [] },
        projection: 'mercator',
        antialias: false,
        bearingSnap: 0,
        bounds,
        pitch: siteId && SITE_MODEL_VIEWS[siteId] ? 40 : 25,
        fitBoundsOptions: { padding: 60, pitch: siteId && SITE_MODEL_VIEWS[siteId] ? 40 : 25 },
        config: {
          basemap: {
            lightPreset: mapStyleMode === 'satellite' ? 'day' : lightPreset,
            ...REPLAY_BASEMAP_3D_SETTINGS,
          },
        },
      });

      const map = mapRef.current;
      const camera = map.cameraForBounds(frame.bounds, { padding: 60, pitch, bearing: 0, maxZoom: 20.5 });
      if (camera?.center) {
        const center = mapboxgl.LngLat.convert(camera.center);
        map.jumpTo({ ...camera, center: frame.restoreCenter([center.lng, center.lat]), bearing: frame.bearing, pitch });
      }
      modeRef.current = mapStyleMode;
      disposeQuality = attachRenderQuality(map);
      map.scrollZoom.setZoomRate(1 / 150);
      resizeObserver = new ResizeObserver(() => map.resize());
      resizeObserver.observe(mapContainerRef.current);
      map.addControl(new MapNavigationControl(), 'bottom-right');

      map.on('load', () => {
      modelsRef.current = [attachApsHqModel, attachCookMarketplaceModel, attachFifteenthStreetModel, attachFrontierTownModel]
        .map(attach => attach(map, mapStyleMode, message => { if (message) setModelError(message); }, lightPreset));

        try { map.setConfigProperty('basemap', 'lightPreset', mapStyleMode === 'satellite' ? 'day' : lightPreset); } catch {}
        (Object.entries(REPLAY_BASEMAP_3D_SETTINGS) as Array<[string, boolean]>).forEach(
          ([key, value]) => { try { map.setConfigProperty('basemap', key, value); } catch {} }
        );

        if (geofenceCoords && geofenceCoords[0] && geofenceCoords[0].length > 2) {
          map.addSource('site-geofence', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: { siteName: patrolVisit.siteName },
              geometry: { type: 'Polygon', coordinates: geofenceCoords },
            },
          });
          map.addLayer({
            id: 'site-geofence-fill', type: 'fill', source: 'site-geofence',
            layout: { visibility: siteId && SITE_MODEL_VIEWS[siteId] ? 'none' : 'visible' },
            paint: { 'fill-color': '#b9cabb', 'fill-opacity': 0.06 },
          });
          map.addLayer({
            id: 'site-geofence-outline', type: 'line', source: 'site-geofence',
            layout: { visibility: siteId && SITE_MODEL_VIEWS[siteId] ? 'none' : 'visible' },
            paint: { 'line-color': '#000000', 'line-width': 2, 'line-opacity': 1 },
          });
        }



        // ENTRY: anchor='bottom' → label floats ABOVE the GPS point
        const entryEl = document.createElement('div');
        entryEl.className = 'replay-point-label replay-point-label--entry';
        entryEl.textContent = 'ENTRY';
        new mapboxgl.Marker({ element: entryEl, anchor: 'bottom' })
          .setLngLat([route[0].longitude, route[0].latitude])
          .addTo(map);

        if (route.length > 1) {
          // EXIT: anchor='top' → label floats BELOW the GPS point
          // Opposite side from ENTRY so they always diverge, even at low zoom
          const exitEl = document.createElement('div');
          exitEl.className = 'replay-point-label replay-point-label--exit';
          exitEl.textContent = 'EXIT';
          new mapboxgl.Marker({ element: exitEl, anchor: 'top' })
            .setLngLat([route[route.length - 1].longitude, route[route.length - 1].latitude])
            .addTo(map);
        }

        const initialHeading =
          route.length >= 2
            ? getHeadingAtTime(currentTimeRef.current, route)
            : 0;
        lastHeadingRef.current = initialHeading;

        const initiallyMoving = isVehicleMoving(currentTimeRef.current, route);
        isMovingRef.current = initiallyMoving;
        const initialPosition =
          getPositionAtTime(currentTimeRef.current, route) || [route[0].longitude, route[0].latitude];

        const el = document.createElement('div');
        el.className = 'marker vehicle-marker replay-marker';
        const label = document.createElement('div');
        label.className = 'vehicle-marker-label';
        label.textContent = markerLabel;
        el.append(label);
        markerRef.current = new ReplayOverlay(map, el, route)
          .setLngLat(initialPosition as [number, number]);
        markerRef.current.setHeading(initialHeading, initiallyMoving);
        updateRouteLine(currentTimeRef.current, route);
        setCurrentSpeed(getSpeedAtTime(currentTimeRef.current, route));
        setMapReady(true);
      });
    } catch (err) {
      console.error('Map init error:', err);
      setError(ERROR_CODES.ERR_MAP_INIT);
    }

    return () => {
      resizeObserver?.disconnect();
      disposeQuality?.();
      modelsRef.current.forEach(model => model.dispose());
      modelsRef.current = [];
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      markerRef.current?.remove();
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [
    hasRoute,
    routeLoading,
    route,
    geofenceCoords,
    patrolVisit.siteName,
    markerLabel,
    movingVehicleIconUrl,
    stoppedVehicleIconUrl,
    entered,
    siteId,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    modelsRef.current.forEach(model => {
      model.setMode(mapStyleMode);
      model.setLightPreset(lightPreset);
    });
    const apply = () => {
      try { if (!map.getStyle()?.imports?.some(item => item.id === 'basemap')) return; } catch { return; }
      if (modeRef.current !== mapStyleMode) {
        modeRef.current = mapStyleMode;
        const current = map.getStyle();
        map.setStyle({ version: 8, sources: current.sources, layers: current.layers,
          imports: [{ id: 'basemap', url: mapStyleMode === 'satellite'
            ? 'mapbox://styles/mapbox/standard-satellite'
            : 'mapbox://styles/mapbox/standard',
            config: { lightPreset: mapStyleMode === 'satellite' ? 'day' : lightPreset, ...REPLAY_BASEMAP_3D_SETTINGS } }],
        }, { diff: false, localFontFamily: undefined, localIdeographFontFamily: 'sans-serif' });
      }
      map.setConfigProperty('basemap', 'lightPreset', mapStyleMode === 'satellite' ? 'day' : lightPreset);
      for (const id of ['site-geofence-fill', 'site-geofence-outline']) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', mapStyleMode === 'standard' && siteId && SITE_MODEL_VIEWS[siteId] ? 'none' : 'visible');
      }
    };
    map.on('style.load', apply);
    map.on('style.import.load', apply);
    apply();
    return () => { map.off('style.load', apply); map.off('style.import.load', apply); };
  }, [mapReady, mapStyleMode, lightPreset, siteId]);

  const getHeadingAtTime = useCallback(replayHeading, []);

  const calculateDistanceMiles = useCallback(
    (lat1: number, lng1: number, lat2: number, lng2: number): number => {
      const R = 3959;
      const toRad = (deg: number) => (deg * Math.PI) / 180;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },
    []
  );

  const speedProfile = useMemo(() => {
    if (!route || route.length < 2) return { segmentSpeeds: [] as number[], maxSpeed: 0 };
    const rawSpeeds: number[] = [];
    for (let i = 0; i < route.length - 1; i++) {
      const p1 = route[i];
      const p2 = route[i + 1];
      if (p2.gapBefore) { rawSpeeds.push(0); continue; }
      if (p1.speed !== undefined && Number.isFinite(p1.speed) && p1.speed >= 0) { rawSpeeds.push(p1.speed); continue; }
      const dtMs = p2.timestamp - p1.timestamp;
      if (dtMs <= 0) { rawSpeeds.push(0); continue; }
      const speedMph =
        calculateDistanceMiles(p1.latitude, p1.longitude, p2.latitude, p2.longitude) /
        (dtMs / 3_600_000);
      rawSpeeds.push(Number.isFinite(speedMph) && speedMph > 0 ? speedMph : 0);
    }
    const positive = rawSpeeds.filter((v) => v > 0).sort((a, b) => a - b);
    const p95 = positive.length ? positive[Math.floor((positive.length - 1) * 0.95)] : 0;
    const hardCap = Math.min(p95 > 0 ? Math.max(20, p95 * 1.25) : 20, 120);
    const clamped = rawSpeeds.map((v) => Math.min(Math.max(v, 0), hardCap));
    const smoothed = clamped.map((_, i) => {
      if (route[i + 1]?.gapBefore) return 0;
      if (route[i].speed !== undefined && Number.isFinite(route[i].speed)) return Math.max(0, route[i].speed!);
      let sum = 0; let count = 0;
      for (let j = Math.max(0, i - 1); j <= Math.min(clamped.length - 1, i + 1); j++) {
        sum += clamped[j]; count++;
      }
      return count > 0 ? sum / count : 0;
    });
    return {
      segmentSpeeds: smoothed,
      maxSpeed: smoothed.reduce((m, v) => Math.max(m, v), 0),
    };
  }, [route, calculateDistanceMiles]);

  const getSpeedAtTime = useCallback(
    (timeOffset: number, routeData: RoutePoint[]): number => {
      if (!routeData || routeData.length < 2 || speedProfile.segmentSpeeds.length === 0) return 0;
      const startTime = routeData[0].timestamp;
      const targetTime = startTime + timeOffset;
      if (targetTime <= routeData[0].timestamp) return speedProfile.segmentSpeeds[0] || 0;
      if (targetTime >= routeData[routeData.length - 1].timestamp) return 0;
      const i = findSegmentIndex(targetTime, routeData);
      const dt = routeData[i + 1].timestamp - routeData[i].timestamp;
      const progress = dt > 0 ? (targetTime - routeData[i].timestamp) / dt : 0;
      const cur = speedProfile.segmentSpeeds[i] || 0;
      const nxt = speedProfile.segmentSpeeds[Math.min(i + 1, speedProfile.segmentSpeeds.length - 1)] || cur;
      const speed = cur + (nxt - cur) * Math.min(Math.max(progress, 0), 1);
      const bounded = Math.min(speedProfile.maxSpeed || speed, Math.max(0, speed));
      return Number.isFinite(bounded) ? bounded : 0;
    },
    [speedProfile]
  );

  const isVehicleMoving = useCallback(
    (timeOffset: number, routeData: RoutePoint[]): boolean => getSpeedAtTime(timeOffset, routeData) > 1,
    [getSpeedAtTime]
  );

  const updateRouteLine = useCallback((timeOffset: number, routeData: RoutePoint[]) => {
    if (!routeData.length) return;
    markerRef.current?.setSegment(findSegmentIndex(routeData[0].timestamp + timeOffset, routeData));
  }, []);

  const updateMarkerIcon = useCallback((heading: number, isMoving: boolean, _forceUpdate = false) => {
    lastHeadingRef.current = heading;
    isMovingRef.current = isMoving;
    markerRef.current?.setHeading(heading, isMoving);
  }, []);

  const toggleFollowVehicle = () => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const next = !followingVehicleRef.current;
    followingVehicleRef.current = next;
    setFollowingVehicle(next);
    map.stop();
    if (next && markerRef.current) {
      const position = markerRef.current.getLngLat();
      const bearing = map.getBearing();
      const turn = ((lastHeadingRef.current - bearing + 540) % 360) - 180;
      map.easeTo({ center: position, zoom: 18.5, pitch: 55,
        bearing: bearing + turn, duration: 850,
        easing: t => t * t * (3 - 2 * t) });
    }
    if (!next && overviewFrameRef.current) {
      const frame = overviewFrameRef.current;
      const pitch = overviewPitchRef.current;
      const camera = map.cameraForBounds(frame.bounds, { padding: 60, pitch, bearing: 0, maxZoom: 20.5 });
      if (camera?.center) {
        const center = mapboxgl.LngLat.convert(camera.center);
        map.easeTo({ ...camera, center: frame.restoreCenter([center.lng, center.lat]),
          bearing: frame.bearing, pitch, duration: 850, easing: t => t * t * (3 - 2 * t) });
      }
    }
  };

  // Animation loop — runs outside React render cycle
  useEffect(() => {
    if (!mapReady || !markerRef.current) return;
    let lastTimestamp: number | null = null;
    let lastCameraTimestamp: number | null = null;
    const updateFollowCamera = (timestamp: number) => {
      const delta = lastCameraTimestamp === null ? 16 : Math.min(64, timestamp - lastCameraTimestamp);
      lastCameraTimestamp = timestamp;
      const map = mapRef.current;
      const position = markerRef.current?.getLngLat();
      if (!followingVehicleRef.current || !map || map.isEasing() || !position || markerRef.current?.getElement().style.visibility === 'hidden') return;
      // A short damped response keeps turns fluid without repeated easeTo
      // animations cancelling one another or delaying vehicle playback.
      const amount = 1 - Math.exp(-delta / 240);
      const turnAmount = 1 - Math.exp(-delta / 700);
      const current = map.getCenter();
      const bearing = map.getBearing();
      const turn = ((lastHeadingRef.current - bearing + 540) % 360) - 180;
      const center: [number, number] = [current.lng + (position.lng - current.lng) * amount,
        current.lat + (position.lat - current.lat) * amount];
      const zoom = map.getZoom() + (18.5 - map.getZoom()) * amount;
      const pitch = map.getPitch() + (55 - map.getPitch()) * amount;
      if (Math.abs(current.lng - center[0]) + Math.abs(current.lat - center[1]) > 1e-9 ||
          Math.abs(zoom - map.getZoom()) > .0001 || Math.abs(pitch - map.getPitch()) > .001 || Math.abs(turn) > .01) {
        map.jumpTo({ center, zoom, pitch, bearing: bearing + turn * turnAmount });
      }
    };
    const routeData = route;
    const duration = routeDuration;
    const speedRef = { current: playbackSpeed };
    speedRef.current = playbackSpeed;

    const animate = (timestamp: number) => {
      if (!isPlayingRef.current) {
        updateFollowCamera(timestamp);
        animationRef.current = requestAnimationFrame(animate);
        lastTimestamp = null;
        return;
      }
      if (lastTimestamp === null) {
        lastTimestamp = timestamp;
        updateFollowCamera(timestamp);
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      const delta = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      const newTime = currentTimeRef.current + delta * speedRef.current;

      if (newTime >= duration) {
        currentTimeRef.current = duration;
        setCurrentTime(duration);
        setIsPlaying(false);
        const endPos = getPositionAtTime(duration, routeData);
        if (endPos && markerRef.current) {
          markerRef.current.getElement().style.visibility = 'visible';
          markerRef.current.setLngLat(endPos);
          updateMarkerIcon(lastHeadingRef.current, false, true);
          updateRouteLine(duration, routeData);
          setCurrentSpeed(0);
        }
      } else {
        currentTimeRef.current = newTime;
        setCurrentTime(newTime);
        const pos = getPositionAtTime(newTime, routeData);
        if (markerRef.current) markerRef.current.getElement().style.visibility = pos ? 'visible' : 'hidden';
        if (!pos) { updateRouteLine(newTime, routeData); setCurrentSpeed(0); }
        if (pos && markerRef.current) {
          markerRef.current.setLngLat(pos);
          updateMarkerIcon(
            getHeadingAtTime(newTime, routeData),
            isVehicleMoving(newTime, routeData)
          );
          updateRouteLine(newTime, routeData);
          setCurrentSpeed(getSpeedAtTime(newTime, routeData));
        }
      }
      updateFollowCamera(timestamp);
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) { cancelAnimationFrame(animationRef.current); animationRef.current = null; }
    };
  }, [mapReady, route, routeDuration, playbackSpeed, getPositionAtTime, getHeadingAtTime, isVehicleMoving, getSpeedAtTime, updateMarkerIcon, updateRouteLine]);

  const updateMarkerPositionAndHeading = (time: number) => {
    const pos = getPositionAtTime(time, route);
    if (markerRef.current) markerRef.current.getElement().style.visibility = pos ? 'visible' : 'hidden';
    if (!pos) updateRouteLine(time, route);
    if (pos && markerRef.current) {
      markerRef.current.setLngLat(pos);
      updateMarkerIcon(getHeadingAtTime(time, route), isVehicleMoving(time, route), true);
      updateRouteLine(time, route);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    currentTimeRef.current = newTime;
    setCurrentTime(newTime);
    updateMarkerPositionAndHeading(newTime);
    setCurrentSpeed(getSpeedAtTime(newTime, route));
  };

  const togglePlay = () => {
    if (currentTime >= routeDuration) {
      currentTimeRef.current = 0;
      setCurrentTime(0);
      updateMarkerPositionAndHeading(0);
    }
    setIsPlaying((p) => !p);
  };

  const resetPlayback = () => {
    setIsPlaying(false);
    currentTimeRef.current = 0;
    setCurrentTime(0);
    updateMarkerPositionAndHeading(0);
  };

  const getCurrentActualTime = () => {
    if (!hasRoute) return '--:--:--';
    return formatActualTime(route[0].timestamp + currentTime);
  };

  const progressPct = routeDuration > 0 ? (currentTime / routeDuration) * 100 : 0;

  const handleReplayMapLayerSelection = (selection: MapLayerSelection) => {
    if (selection === 'satellite') {
      setMapStyleMode('satellite');
      return;
    }
    setLightPreset(selection);
    setMapStyleMode('standard');
  };

  return (
    <div className="modal-overlay patrol-viewer-overlay" onClick={onClose}>
      <div className="patrol-replay-modal patrol-viewer" role="dialog" aria-modal="true" aria-labelledby="patrol-viewer-title" ref={dialogRef} tabIndex={-1} onKeyDown={event => {
        if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
        if (event.key === 'Tab') {
          const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select, [tabindex="0"]')).filter(el => el.getClientRects().length > 0);
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === event.currentTarget)) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      }} onClick={(e) => e.stopPropagation()}>

        <div className="modal-header">
          <div className="replay-header-info">
            <h2 id="patrol-viewer-title" className="replay-viewer-title">{patrolVisit.siteName}</h2>
            <div className="replay-subtitle">
              <span className="replay-vehicle">{vehicleLabel}</span>

            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close replay"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
        </div>

        <div className="replay-content">
          {routeLoading && <div role="status">Loading patrol route...</div>}
          {error ? (
            <div className="replay-error">
              <div className="replay-error-icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div className="error-message">{error}</div>
              <div className="error-hint">
                {error === ERROR_CODES.ERR_NO_ROUTE &&
                  'This patrol visit has no GPS route data recorded. Route tracking starts collecting GPS points when a vehicle enters a site. Older patrols before this feature was enabled will not have route data.'}
                {error === ERROR_CODES.ERR_SINGLE_POINT &&
                  'Only one GPS point was recorded. The vehicle may have been stationary or the patrol was too short to capture movement.'}
                {error === ERROR_CODES.ERR_MAP_INIT && 'Failed to initialize the map. Please try again.'}
                {error === ERROR_CODES.ERR_INVALID_COORDS && 'The GPS coordinates in this patrol are invalid.'}
              </div>
            </div>
          ) : (
            <>
              <div className="replay-map-shell">
                <div className="replay-map-overlay">
                  <ReplayMapStyleDropdown
                    selection={currentMapLayerSelection}
                    options={replayLayerOptions}
                    onChange={handleReplayMapLayerSelection}
                  />
                  <button type="button" className="replay-follow-toggle"
                    disabled={!mapReady} aria-pressed={followingVehicle}
                    aria-label={followingVehicle ? 'Show full route' : 'Follow vehicle'}
                    title={followingVehicle ? 'Show full route' : 'Follow vehicle'}
                    onClick={toggleFollowVehicle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      {followingVehicle ? <path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M8 8l8 8m0-8-8 8" /> : <><path d="M12 3v3m0 12v3M3 12h3m12 0h3" /><circle cx="12" cy="12" r="7" /><path d="m12 8 3 7-3-1-3 1z" /></>}
                    </svg>
                  </button>
                </div>
                <div className={`replay-map-container${mapReady ? ' is-ready' : ''}`} ref={mapContainerRef} />
                {!mapReady && <div className="replay-map-loading" role="status">Preparing patrol map…</div>}
                {inGpsGap && <div className="replay-model-warning" role="status">GPS gap — location unknown</div>}
                {!inGpsGap && locationStatus && <div className="replay-model-warning" role="status">
                  {locationStatus.kind === 'held'
                    ? `Last known position · GPS updated ${Math.floor(locationStatus.ageMs / 60000)}m ${Math.floor(locationStatus.ageMs / 1000) % 60}s ago`
                    : 'Position interpolated between recorded GPS points'}
                </div>}
                {modelError && <div className="replay-model-warning" role="status">{modelError}</div>}
              </div>

              <div className="replay-controls">
                {/* Progress scrubber */}
                <div className="replay-progress">
                  <div
                    className="progress-slider-track"
                    style={{ '--progress-pct': `${progressPct}%` } as React.CSSProperties}
                  >
                    <input
                      aria-label="Patrol replay position"
                      disabled={!mapReady}
                      type="range"
                      min="0"
                      max={routeDuration}
                      value={currentTime}
                      onChange={handleSeek}
                      className="progress-slider"
                    />
                  </div>
                  <div className="replay-progress-times">
                    <span className="replay-progress-elapsed">{formatTime(currentTime)}</span>
                    <span className="replay-progress-total">{formatTime(routeDuration)}</span>
                  </div>
                </div>

                {/* Button row */}
                <div className="replay-buttons">
                  <button className="replay-btn" disabled={!mapReady} onClick={resetPlayback} title="Reset to start" aria-label="Reset">
                    <span className="replay-btn-glyph" aria-hidden="true">↺</span>
                  </button>

                  <button className="replay-btn primary" disabled={!mapReady} onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
                    {isPlaying ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </button>

                  <div className="speed-control">
                    <span className="speed-control-label">Speed</span>
                    <SpeedDropdown value={playbackSpeed} onChange={setPlaybackSpeed} />
                  </div>

                  <div className="replay-controls-right">
                    <span className="replay-clock">{getCurrentActualTime()}</span>
                    <div className="replay-speed-display">
                      <span className="speed-value">{inGpsGap ? "—" : Math.round(currentSpeed)}</span>
                      <span className="speed-unit">mph</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="replay-stats">
                <div className="stat-item">
                  <span className="stat-label">Entry</span>
                  <span className="stat-value">{formatActualTime(patrolVisit.entryTime)}</span>
                </div>
                {patrolVisit.exitTime && (
                  <div className="stat-item">
                    <span className="stat-label">Exit</span>
                    <span className="stat-value">{formatActualTime(patrolVisit.exitTime)}</span>
                  </div>
                )}
                <div className="stat-item">
                  <span className="stat-label">Duration</span>
                  <span className="stat-value">{formatTime(routeDuration)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">GPS Points</span>
                  <span className="stat-value">{route.length.toLocaleString()}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatrolReplayPlayer;

